import React, { useRef, useState } from 'react';
import { RefreshCw, ShieldCheck, Stethoscope, Upload } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent, CardHeader } from '@components/Card';
import { Input } from '@components/Input';
import { CertificateLog } from '@components/certificates/CertificateLog';
import { CertificateResultCard } from '@components/certificates/CertificateResultCard';
import { companyService } from '@services/companyService';
import { CertificateResult } from '@/types/certificate';
import { Company } from '@/types/company';
import { formatDateBR } from '@pages/financial/financialUtils';
import { certificateBadgeClass, certificateStatus, CERTIFICATE_WARNING_DAYS, daysUntilISO } from '@/utils/certificateStatus';

interface Props {
  companyId: number;
  company: Company | null;
  /** Chamado depois de um envio bem-sucedido, para a tela reler a empresa. */
  onUploaded: () => void;
}

/**
 * Certificado digital A1 (e-CNPJ) da empresa — o que assina NF-e junto à SEFAZ
 * e os eventos do e-Social.
 *
 * Até ago/2026 o arquivo era um Secret do AKS montado no pod, e trocá-lo exigia
 * `kubectl create secret` + rollout nos dois namespaces. Agora ele é enviado por
 * aqui e guardado num container privado do Blob Storage.
 *
 * Não existe "Renovar certificado", ao contrário dos certificados do Itaú: o A1
 * é emitido numa homologação com a Autoridade Certificadora, que devolve um
 * arquivo novo. O que esta tela faz é o último passo — colocá-lo em produção.
 */
export const CompanyCertificateCard: React.FC<Props> = ({ companyId, company, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'upload' | 'test' | null>(null);
  const [result, setResult] = useState<CertificateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logVersion, setLogVersion] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const expiresAt = company?.certificate_expires_at ?? null;
  const hasCertificate = Boolean(company?.has_certificate);
  // Sem certificado é "missing"; com certificado e sem data é "unknown" — são
  // situações diferentes e pedem mensagens diferentes.
  const status = hasCertificate && !expiresAt ? 'unknown' : certificateStatus(expiresAt);
  const days = expiresAt ? daysUntilISO(expiresAt) : undefined;

  const handleUpload = async () => {
    if (!file) {
      setError('Selecione o arquivo .pfx do certificado.');
      return;
    }
    if (!password.trim()) {
      setError('Informe a senha do certificado.');
      return;
    }
    try {
      setBusy('upload');
      setError(null);
      setResult(null);
      const res = await companyService.uploadCertificate(companyId, file, password);
      setResult(res);
      // A senha nunca fica na tela depois de enviada.
      setPassword('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLogVersion((v) => v + 1);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar o certificado');
    } finally {
      setBusy(null);
    }
  };

  const handleTest = async () => {
    try {
      setBusy('test');
      setError(null);
      setResult(null);
      setResult(await companyService.testCertificate(companyId));
      setLogVersion((v) => v + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao testar o certificado');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Certificado Digital A1"
        subtitle="Certificado e-CNPJ usado para assinar NF-e (SEFAZ) e os eventos do e-Social."
      />
      <CardContent>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium ${certificateBadgeClass(status)}`}
          >
            <ShieldCheck size={14} />
            {statusLabel(status, expiresAt, days)}
          </span>
          {company?.certificate_subject_cn && (
            <span className="text-xs text-slate-500 font-mono">{company.certificate_subject_cn}</span>
          )}
        </div>

        {hasCertificate && company?.certificate_uploaded_at && (
          <p className="text-xs text-slate-500 mb-3">
            Último envio em {formatDateBR(company.certificate_uploaded_at)}
            {company.certificate_uploaded_by ? ` por ${company.certificate_uploaded_by}` : ''}.
          </p>
        )}

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {result && <CertificateResultCard result={result} />}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Arquivo .pfx</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pfx,.p12"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-emerald-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100"
            />
          </div>
          <div>
            <Input
              label="Senha do certificado"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </div>
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleUpload}
              loading={busy === 'upload'}
              disabled={busy !== null}
              icon={<Upload size={16} />}
            >
              {hasCertificate ? 'Substituir certificado' : 'Enviar certificado'}
            </Button>
          </div>
        </div>

        {hasCertificate && (
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleTest}
              loading={busy === 'test'}
              disabled={busy !== null}
              icon={<Stethoscope size={16} />}
            >
              Testar certificado
            </Button>
            <span className="text-xs text-slate-500">
              Consulta o status do serviço na SEFAZ com este certificado. Não emite nem consome
              numeração.
            </span>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-3">
          <RefreshCw size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            O arquivo e a senha são enviados juntos e validados antes de qualquer gravação: senha
            errada, arquivo inválido ou certificado de outra empresa são recusados sem alterar nada.
            O A1 <strong>não tem renovação automática</strong> — quando vencer, é preciso emitir um
            novo com a Autoridade Certificadora e enviá-lo aqui. O aviso de vencimento aparece no
            painel do dashboard com {CERTIFICATE_WARNING_DAYS} dias de antecedência.
          </p>
        </div>

        <CertificateLog
          fetchLogs={() => companyService.getCertificateLogs(companyId)}
          refreshKey={logVersion}
          emptyLabel="Nenhuma operação registrada ainda para esta empresa."
        />
      </CardContent>
    </Card>
  );
};

const statusLabel = (
  status: ReturnType<typeof certificateStatus> | 'unknown',
  expiresAt: string | null,
  days?: number,
): string => {
  switch (status) {
    case 'missing':
      return 'Sem certificado enviado';
    case 'unknown':
      return 'Certificado enviado, validade não registrada';
    case 'expired':
      return `Vencido em ${formatDateBR(expiresAt!)}`;
    case 'expiring':
      return `Vence em ${days} dia(s) — ${formatDateBR(expiresAt!)}`;
    default:
      return `Válido até ${formatDateBR(expiresAt!)}`;
  }
};
